import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { useExamSubjectOptions, useOfstedGradeOptions } from "./useExamTypeOptions";
import RangeSlider from "./RangeSlider";
import { CATCHMENT_FILTER_NONE } from "./applyFilters";

const shortSubjectLabel = (s) => s.replace(/ reasoning$/i, "");

const CATCHMENT_DISPLAY_LABELS = {
  Designated: "Fixed Catchment",
  Open: "Open to All",
  Both: "Fixed + Open",
  [CATCHMENT_FILTER_NONE]: "Possible / no catchment data",
};

const CATCHMENT_ORDER = ["Open", "Designated", "Both", CATCHMENT_FILTER_NONE];

function CheckboxGroup({ label, options, selected, onChange, inline }) {
  const toggle = (val) => {
    const next = selected.includes(val)
      ? selected.filter((v) => v !== val)
      : [...selected, val];
    onChange(next);
  };

  return (
    <div className={`v2-filter-field${inline ? " v2-filter-field--inline" : ""}`}>
      <label className="v2-filter-label">{label}</label>
      <div className="v2-filter-checkbox-group">
        {options.map((opt) => (
          <label key={opt} className="v2-filter-checkbox">
            <input
              type="checkbox"
              checked={selected.includes(opt)}
              onChange={() => toggle(opt)}
            />
            <span>{opt}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function MultiSelectDropdown({ label, options, selected, onChange, placeholder, inline, displayLabels }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);

  const getLabel = useCallback((val) => (displayLabels && displayLabels[val]) || val, [displayLabels]);

  const handleClickOutside = useCallback((e) => {
    if (wrapRef.current && !wrapRef.current.contains(e.target)) {
      setOpen(false);
      setSearch("");
    }
  }, []);

  useEffect(() => {
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [open, handleClickOutside]);

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter((o) => getLabel(o).toLowerCase().includes(q));
  }, [options, search, getLabel]);

  const toggle = (val) => {
    const next = selected.includes(val)
      ? selected.filter((v) => v !== val)
      : [...selected, val];
    onChange(next);
  };

  const summary = selected.length === 0
    ? (placeholder || "All")
    : selected.length === 1
      ? getLabel(selected[0])
      : `${selected.length} selected`;

  return (
    <div className={`v2-filter-field${inline ? " v2-filter-field--inline" : ""}`} ref={wrapRef}>
      <label className="v2-filter-label">{label}</label>
      <div className="v2-multiselect">
        <button
          type="button"
          className={`v2-filter-select v2-multiselect-trigger${selected.length > 0 ? " v2-multiselect-trigger--active" : ""}`}
          onClick={() => { setOpen((v) => !v); setSearch(""); }}
        >
          {summary}
        </button>
        {open ? (
          <div className="v2-multiselect-dropdown">
            <input
              type="text"
              className="v2-multiselect-search"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
            <div className="v2-multiselect-options">
              {filtered.map((opt) => (
                <label key={opt} className="v2-multiselect-option">
                  <input
                    type="checkbox"
                    checked={selected.includes(opt)}
                    onChange={() => toggle(opt)}
                  />
                  <span>{getLabel(opt)}</span>
                </label>
              ))}
              {filtered.length === 0 ? (
                <div className="v2-multiselect-empty">No matches</div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function SchoolProfileFilterPanel({ filters, updateFilter, schools }) {
  const examSubjects = useExamSubjectOptions();
  const ofstedGrades = useOfstedGradeOptions();

  const schoolTypes = useMemo(() => {
    const set = new Set();
    (schools || []).forEach((s) => {
      if (s.school_type) set.add(s.school_type);
    });
    return [...set].sort();
  }, [schools]);

  const localAuthorities = useMemo(() => {
    const set = new Set();
    (schools || []).forEach((s) => {
      if (s.local_authority) set.add(s.local_authority);
    });
    return [...set].sort();
  }, [schools]);

  const catchmentCategories = useMemo(() => {
    const set = new Set();
    let hasNearbyNoCatchment = false;
    (schools || []).forEach((s) => {
      if (s.catchment_category) set.add(s.catchment_category);
      const cat = String(s.catchment_category || "").trim().toLowerCase();
      if (!s.has_catchment && cat !== "open" && cat !== "both") {
        hasNearbyNoCatchment = true;
      }
    });
    return CATCHMENT_ORDER.filter((c) =>
      c === CATCHMENT_FILTER_NONE ? hasNearbyNoCatchment : set.has(c)
    );
  }, [schools]);

  return (
    <div className="v2-filter-panel">
      <CheckboxGroup
        label="Gender"
        options={["Boys", "Girls", "Mixed"]}
        selected={filters.gender}
        onChange={(v) => updateFilter("gender", v)}
        inline
      />

      <MultiSelectDropdown
        label="School Type"
        options={schoolTypes}
        selected={filters.schoolType}
        onChange={(v) => updateFilter("schoolType", v)}
        placeholder="All types"
        inline
      />

      <MultiSelectDropdown
        label="Local Authority"
        options={localAuthorities}
        selected={filters.localAuthority}
        onChange={(v) => updateFilter("localAuthority", v)}
        placeholder="All areas"
        inline
      />

      <MultiSelectDropdown
        label="Catchment Type"
        options={catchmentCategories}
        selected={filters.catchmentCategory}
        onChange={(v) => updateFilter("catchmentCategory", v)}
        placeholder="All categories"
        inline
        displayLabels={CATCHMENT_DISPLAY_LABELS}
      />

      <MultiSelectDropdown
        label="Ofsted Rating"
        options={ofstedGrades}
        selected={filters.ofstedRating}
        onChange={(v) => updateFilter("ofstedRating", v)}
        placeholder="All ratings"
        inline
      />

      <RangeSlider
        label="Oxbridge Offers"
        value={filters.oxbridgeOffers}
        onChange={(v) => updateFilter("oxbridgeOffers", v)}
        min={0}
        max={30}
        step={1}
        displayValue={filters.oxbridgeOffers === 0 ? "Any" : `${filters.oxbridgeOffers}+`}
      />

      {examSubjects.length > 0 ? (
        <div className="v2-filter-field v2-filter-field--inline v2-filter-field--inline-top">
          <label className="v2-filter-label">11+ Exam Subjects</label>
          <div className="v2-filter-checkbox-rows">
            <div className="v2-filter-checkbox-row">
              {examSubjects.filter((s) => ["English", "Maths", "Verbal reasoning"].includes(s)).map((opt) => (
                <label key={opt} className="v2-filter-checkbox">
                  <input
                    type="checkbox"
                    checked={filters.examSubjects.includes(opt)}
                    onChange={() => {
                      const next = filters.examSubjects.includes(opt)
                        ? filters.examSubjects.filter((v) => v !== opt)
                        : [...filters.examSubjects, opt];
                      updateFilter("examSubjects", next);
                    }}
                  />
                  <span>{shortSubjectLabel(opt)}</span>
                </label>
              ))}
            </div>
            <div className="v2-filter-checkbox-row">
              {examSubjects.filter((s) => ["Non-verbal reasoning", "Spatial reasoning"].includes(s)).map((opt) => (
                <label key={opt} className="v2-filter-checkbox">
                  <input
                    type="checkbox"
                    checked={filters.examSubjects.includes(opt)}
                    onChange={() => {
                      const next = filters.examSubjects.includes(opt)
                        ? filters.examSubjects.filter((v) => v !== opt)
                        : [...filters.examSubjects, opt];
                      updateFilter("examSubjects", next);
                    }}
                  />
                  <span>{shortSubjectLabel(opt)}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
